import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HashRouter, Routes, Route } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { DesktopControls } from "@/components/DesktopControls";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import DietPlans from "@/pages/DietPlans";
import DietPlanEditor from "@/pages/DietPlanEditor";
import Workouts from "@/pages/Workouts";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import NotFound from "@/pages/NotFound";
import Finance from "@/pages/Finance";
import Auth from "@/pages/Auth";
import ClientVendor from "@/pages/clientVendor";
import GymPlan from "@/pages/gymPlan";
import Calendar from "@/pages/calendar";
import DietPage from "@/pages/DietPage";
import Accounts from "@/pages/Accounts";

import AssessmentsPage from "@/pages/AssessmentsPage";
import DoctorAdvice from "@/pages/DoctorAdvice";
import WebsiteAdmin from "./pages/WebsiteAdmin";
const queryClient = new QueryClient();

// Create a desktop features page
const DesktopFeatures = () => (
  <div className="container py-8">
    <h1 className="text-3xl font-bold mb-6">Desktop Features</h1>
    <p className="text-gray-500 mb-8">
      This page demonstrates desktop-specific features available in the Electron app.
    </p>
    <div className="grid gap-8">
      <DesktopControls />
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
      <Toaster />
      <Sonner />
      <HashRouter>
        <Routes>
          {/* Landing page - no layout */}
          <Route path="/" element={<Landing />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/client-vendor" element={<ClientVendor />} />
          <Route path="/gym-plan" element={<GymPlan />} />
          <Route path="/calendar" element={<Calendar />} />
          <Route path="/diet" element={<DietPage />} />
  
          {/* Diet plan editor - full screen, no sidebar */}
          <Route path="/diet-editor" element={
            <ProtectedRoute>
              <DietPlanEditor />
            </ProtectedRoute>
          } />

          <Route path="/doctor-advice" element={<DoctorAdvice />} />
          <Route path="/assessments" element={<AssessmentsPage />} />
          <Route path="/assessments/exam/:examId" element={<AssessmentsPage />} />
          <Route path="/website-admin" element={<WebsiteAdmin />} />
          
          {/* Admin routes - with layout and authentication */}
          <Route path="/LODYadmin" element={
            <ProtectedRoute>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYdashboard" element={
            <ProtectedRoute>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYClients" element={
            <ProtectedRoute>
              <Layout><Clients /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYClients/:id" element={
            <ProtectedRoute>
              <Layout><ClientDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYDietPlans" element={
            <ProtectedRoute>
              <Layout><DietPlans /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYWorouts" element={
            <ProtectedRoute>
              <Layout><Workouts /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYAnalytics" element={
            <ProtectedRoute>
              <Layout><Analytics /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYSettings" element={
            <ProtectedRoute>
              <Layout><Settings /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYSettings/exam/:examId" element={
            <ProtectedRoute>
              <Layout><Settings /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYFinance" element={
            <ProtectedRoute>
              <Layout><Finance /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYAccounts" element={
            <ProtectedRoute>
              <Layout><Accounts /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/LODYDesktop" element={
            <ProtectedRoute>
              <Layout><DesktopFeatures /></Layout>
            </ProtectedRoute>
          } />
          
          {/* Legacy routes for backward compatibility - also protected */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout><Dashboard /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients" element={
            <ProtectedRoute>
              <Layout><Clients /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/clients/:id" element={
            <ProtectedRoute>
              <Layout><ClientDetail /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/diets" element={
            <ProtectedRoute>
              <Layout><DietPlans /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/workouts" element={
            <ProtectedRoute>
              <Layout><Workouts /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <Layout><Analytics /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Layout><Settings /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/finance" element={
            <ProtectedRoute>
              <Layout><Finance /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/accounts" element={
            <ProtectedRoute>
              <Layout><Accounts /></Layout>
            </ProtectedRoute>
          } />
          <Route path="/desktop" element={
            <ProtectedRoute>
              <Layout><DesktopFeatures /></Layout>
            </ProtectedRoute>
          } />
          
          <Route path="*" element={<NotFound />} />
        </Routes>
      </HashRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
